/**
 * The Getting Started page's rules, pure so they are tested without the page: the stage list rows
 * (the Menu Import stage bar's tones: done, attention, todo, optional), where to resume, and Back /
 * Next. The API decides each step's status and lock (GettingStartedSteps.cs); this only presents it.
 */
import type { GettingStartedState, GettingStartedStep } from '../../api/gettingStartedApi';
import { STEP_INFO, GO_LIVE } from './steps';

export type StageTone = 'done' | 'attention' | 'todo' | 'optional';

export interface StageRow {
  key: string;
  /** "B0" ... "B11" are internal; the list shows 1 ... 12. */
  number: number;
  label: string;
  tone: StageTone;
  /** A few words under the label. */
  statusText: string;
  /** Why it cannot be opened yet. */
  locked: string | null;
}

export function statusText(step: GettingStartedStep): string {
  switch (step.status) {
    case 'done':
      return 'Done';
    case 'skipped':
      return 'Skipped';
    case 'optional':
      return 'Optional';
    case 'attention':
      return step.check && !step.check.ok ? step.check.message : 'Needs a look';
    default:
      return step.marked === 'todo' ? 'Started' : 'Not started';
  }
}

export function toneOf(step: GettingStartedStep): StageTone {
  if (step.status === 'done') return 'done';
  if (step.status === 'attention') return 'attention';
  if (step.status === 'optional' || step.status === 'skipped') return 'optional';
  return 'todo';
}

export function stageRows(steps: GettingStartedStep[]): StageRow[] {
  return steps.map((s, i) => ({
    key: s.key,
    number: i + 1,
    label: STEP_INFO.find((x) => x.key === s.key)?.label ?? s.title,
    tone: toneOf(s),
    statusText: statusText(s),
    locked: s.locked,
  }));
}

const finished = (s: GettingStartedStep) => s.status === 'done' || s.status === 'skipped' || s.status === 'optional';

/** Where to pick up: the first open step not finished, else Go-live. */
export function resumeStep(steps: GettingStartedStep[]): string {
  const next = steps.find((s) => !s.locked && !finished(s));
  return next?.key ?? steps.find((s) => s.key === GO_LIVE)?.key ?? steps[0]?.key ?? '';
}

/** The step before or after this one that can be opened, or null at either end (or behind a lock). */
export function neighbour(steps: GettingStartedStep[], key: string, direction: -1 | 1): string | null {
  const i = steps.findIndex((s) => s.key === key);
  if (i < 0) return null;
  const j = i + direction;
  if (j < 0 || j >= steps.length) return null;
  return steps[j].locked ? null : steps[j].key;
}

/** The step named in the address when it exists and is open, else null (the page then resumes). */
export function openableStep(steps: GettingStartedStep[], key: string | undefined): string | null {
  if (!key) return null;
  const s = steps.find((x) => x.key === key);
  return s && !s.locked ? s.key : null;
}

/** Steps finished, out of the ones that count (the optional one counts only once someone does it). */
export function progress(steps: GettingStartedStep[]): { done: number; total: number } {
  const counted = steps.filter((s) => !(s.optional && s.status === 'optional'));
  return { done: counted.filter((s) => s.status === 'done' || s.status === 'skipped').length, total: counted.length };
}

/** The dashboard's Setup checklist and the Setup launcher card show while the wizard is on and not signed off. */
export function showSetupChecklist(state: GettingStartedState | undefined): boolean {
  return !!state?.enabled && !!state.steps?.length && !state.finished;
}
